// src/pages/RestaurantPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight, CheckCircle, Zap, DollarSign, Clock,
  Utensils, Users, Calendar, MessageSquare, TrendingUp, AlertTriangle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../hooks/useLanguage";

const PAIN_ICONS = [Calendar, Users, Utensils, MessageSquare, TrendingUp, AlertTriangle];
const PAIN_STYLES = [
  { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
  { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  { color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  { color: "text-[#3b82f6]", bg: "bg-blue-500/10", border: "border-blue-500/20" },
];

const TOOL_META = [
  { name: "7shifts", score: 93, color: "bg-orange-500" },
  { name: "SevenRooms", score: 89, color: "bg-blue-500" },
  { name: "Olo", score: 85, color: "bg-blue-500" },
  { name: "Jasper AI", score: 82, color: "bg-purple-500" },
];

const CONTENT = {
  en: {
    title: "AI Blueprint for Restaurants — Cut Costs, Fill Seats, Stop Guessing",
    metaDesc: "Get a personalized AI implementation blueprint for your restaurant. Exactly which tools to use, in what order, with real ROI projections. Built from 50K+ restaurant data.",
    heroBadge: "Built for restaurant owners",
    heroTitlePart1: "Which AI Tools Will Cut Costs",
    heroTitlePart2: "and Fill More Seats?",
    heroSubtitlePre: "Answer 8 questions about your restaurant. Get a personalized blueprint with the",
    heroSubtitleStrong: "exact tools, setup order, and ROI projections",
    heroSubtitlePost: "— built from {count} vetted AI tools and 2026 restaurant industry benchmarks.",
    heroBullets: [
      "Cut no-shows by 67% with AI reservation management",
      "Reduce scheduling time by 80% and labor costs by 23%",
      "Lift online order AOV by 31% with AI upsell flows",
      "Automate social media and review responses in your brand voice",
    ],
    heroCta: "Cut My No-Shows by 67%",
    heroCtaFine: "One-time — No subscription — Ready in minutes",
    painEyebrow: "Sound familiar?",
    painHeading: "The 6 Profit Drains in Most Restaurants",
    painSub: "AI can fix all of these — but only if you know which tools to use and in what order. That's what the blueprint tells you.",
    painPoints: [
      { title: "No-shows and last-minute cancellations", desc: "Restaurants lose $224/table/year to no-shows. AI-powered reservation systems with smart deposits and automated reminders cut no-shows by 67%.", stat: "67% fewer no-shows" },
      { title: "Labor scheduling is a manual nightmare", desc: "Overstaffing wastes $3,200/month. AI scheduling tools like 7shifts reduce scheduling time by 80% and cut labor overages by 23%.", stat: "23% labor cost reduction" },
      { title: "Online ordering losing to competitors", desc: "Restaurants using AI-optimized online menus and upsell flows see 31% higher average order values. Most restaurants still use static menus.", stat: "31% higher AOV" },
      { title: "Customer reviews go unanswered", desc: "89% of diners read reviews before visiting. AI tools auto-respond to reviews in your voice within minutes — 24/7 — so you never miss one.", stat: "89% of diners read reviews" },
      { title: "Marketing is inconsistent and time-consuming", desc: "Restaurants posting 3+ times/week get 2x the foot traffic. AI tools cut social media prep from 4 hours/week to 20 minutes.", stat: "4 hrs → 20 min/week" },
      { title: "Food waste eating into already thin margins", desc: "Average restaurant wastes 4—10% of food purchased. AI demand forecasting cuts waste by up to 35%, protecting margins on both ends.", stat: "35% waste reduction" },
    ],
    toolEyebrow: "Tool stack preview",
    toolHeading: "Tools Your Blueprint Could Recommend",
    toolSub: "These are examples. Your blueprint picks from {count}+ tools based on your restaurant's specific problems, size, and budget.",
    toolStack: [
      { category: "AI Scheduling", price: "From $29/mo", why: "Builds optimal schedules from sales forecasts. Cuts scheduling time from 4 hours to 20 minutes." },
      { category: "Reservations & CRM", price: "Custom pricing", why: "AI-powered reservation and waitlist management. Reduces no-shows by 67% with automated reminders and smart deposit flows." },
      { category: "Online Ordering", price: "From $249/mo", why: "AI menu optimization and upsell flows lift online order AOV by 31%. Integrates with all major POS systems." },
      { category: "Marketing & Copy", price: "From $39/mo", why: "Generates social posts, menu descriptions, and email campaigns in your brand voice. Cut marketing prep from 4 hrs to 20 min/week." },
    ],
    toolMatch: "% match",
    toolFootnote: "Your blueprint selects 5—8 tools from a database of {count}+, ranked for your specific restaurant.",
    getEyebrow: "What you get",
    getHeading: "Your ${price} Restaurant AI Blueprint Includes",
    whatYouGet: [
      { title: "Your exact restaurant AI tool stack", desc: "5—8 tools matched to your restaurant type, size, and specific problems. With real pricing, setup steps, and what to skip." },
      { title: "12-week implementation roadmap", desc: "Phase 1 targets your biggest revenue leak — usually no-shows or labor costs. Week-by-week tasks, not overwhelming to-do lists." },
      { title: "Custom prompt library for restaurants", desc: "15—30 prompts for menu descriptions, social posts, review responses, staff communications, and marketing copy. Ready to paste." },
      { title: "ROI projections for your restaurant", desc: "Expected labor savings, revenue lift from online ordering, and hours saved — built from 2026 restaurant industry benchmarks." },
      { title: "Risk guide and common mistakes", desc: "What goes wrong when restaurants adopt AI tools too fast, and how to avoid it. Protect your margins while modernizing." },
      { title: "First 30 days checklist", desc: "Day-by-day tasks to see real results in your first month — fewer no-shows, faster scheduling, or better online reviews." },
    ],
    bottomHeading1: "Stop Losing Money to No-Shows.",
    bottomHeading2: "Start Running a Smarter Restaurant.",
    bottomSub: "Answer 8 questions about your restaurant. Get a blueprint with your exact tool stack, setup order, and week-by-week roadmap. Ready in minutes.",
    bottomCta: "Get My Restaurant AI Blueprint",
    bottomFineReady: "Ready in minutes",
    bottomFineOneTime: "One-time ${price}",
    bottomFineNoSub: "No subscription",
    guarantee: "60-day money-back guarantee if it doesn't deliver value.",
    preview: {
      eyebrow: "AI Implementation Blueprint",
      industryLabel: "Restaurant — Food & Hospitality",
      reportReady: "Report Ready",
      kpis: [["$8–38K", "Year 1 ROI"], ["6 hrs", "Saved/week"], ["5–8 tools", "Recommended"]],
      topTools: "Top Tools For Your Restaurant",
      toolBadges: ["Scheduling — $29/mo", "Reservations — Custom", "Online Orders — $249/mo"],
      roadmap: "12-Week Roadmap",
      phases: ["Phase 1\nNo-shows & Labor", "Phase 2\nOnline Orders", "Phase 3\nMarketing"],
      floatTop: "Built in 3 min",
      floatBottom: "Restaurant-optimized",
      matchSuffix: "% match",
    },
    faqEyebrow: "Frequently asked",
    faqHeading: "Before you build your blueprint",
    faq: [
      { q: "I run a small taqueria / fine-dining concept / ghost kitchen / multi-unit. Does this still apply?", a: "Yes. The blueprint asks about your concept, cover count, and biggest pain — then tailors the stack. Ghost kitchens get delivery-margin and aggregator tools; full-service gets reservation and labor-forecasting tools; multi-unit gets inventory-sync and ops-reporting tools." },
      { q: "How long does it take to actually implement the blueprint?", a: "It's a 12-week roadmap built for owner-operators with 2—4 hours a week to invest. Phase 1 goes after your biggest leak first — usually labor scheduling or food cost — and most restaurants see measurable results in 2—3 weeks before moving on." },
      { q: "I've tried AI tools and they didn't pay off. Why is this different?", a: "Most operators buy a tool before defining the bottleneck, skip the POS / reservations integration step, and never measure cost-per-cover impact. The blueprint flips that: ranks tools by fit to your concept, maps each to your POS (Toast, Square, Clover, Lightspeed) and your reservations system (Resy, OpenTable, Tock, SevenRooms), and projects ROI per service before you sign a contract." },
      { q: "Is my customer and sales data safe?", a: "Every tool recommendation includes a data-handling note — where customer data is stored, whether it's shared with third parties, and PCI / privacy flags to watch. Anything that looks sketchy on data ownership is called out, not quietly recommended." },
      { q: "What if the blueprint doesn't fit my restaurant?", a: "60-day money-back guarantee, no questions asked. Email us within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you couldn't use." },
    ],
  },
  es: {
    title: "Blueprint de IA para Restaurantes — Baja Costos, Llena Mesas, Deja de Improvisar",
    metaDesc: "Recibe un blueprint personalizado de implementación de IA para tu restaurante. Qué herramientas usar, en qué orden, con proyecciones reales de ROI. Armado con datos de 50K+ restaurantes.",
    heroBadge: "Hecho para dueños de restaurantes",
    heroTitlePart1: "¿Qué Herramientas de IA Bajan Costos",
    heroTitlePart2: "y Llenan Más Mesas?",
    heroSubtitlePre: "Responde 8 preguntas sobre tu restaurante. Recibe un blueprint personalizado con las",
    heroSubtitleStrong: "herramientas exactas, el orden de implementación y proyecciones de ROI",
    heroSubtitlePost: "— armado con {count} herramientas de IA verificadas y benchmarks de restaurantes de 2026.",
    heroBullets: [
      "Baja 67% las ausencias con gestión de reservas por IA",
      "Reduce el tiempo de programación 80% y el costo laboral 23%",
      "Sube el ticket promedio online 31% con flujos de upsell con IA",
      "Automatiza redes sociales y respuestas a reseñas en tu voz de marca",
    ],
    heroCta: "Baja Mis Ausencias 67%",
    heroCtaFine: "Pago único — Sin suscripción — Listo en minutos",
    painEyebrow: "¿Te suena familiar?",
    painHeading: "Las 6 Fugas de Ganancia en la Mayoría de los Restaurantes",
    painSub: "La IA puede resolver todo esto — pero solo si sabes qué herramientas usar y en qué orden. Eso es lo que te dice el blueprint.",
    painPoints: [
      { title: "Ausencias y cancelaciones de último minuto", desc: "Los restaurantes pierden $224/mesa/año por ausencias. Los sistemas de reservas con IA, con depósitos inteligentes y recordatorios automáticos, bajan las ausencias un 67%.", stat: "67% menos ausencias" },
      { title: "La programación de turnos es una pesadilla manual", desc: "Sobrecontratar cuesta $3,200/mes. Herramientas de IA como 7shifts reducen el tiempo de programación un 80% y cortan el exceso laboral un 23%.", stat: "23% menos costo laboral" },
      { title: "Los pedidos online los están ganando tus competidores", desc: "Los restaurantes con menús online optimizados por IA y flujos de upsell ven un ticket promedio 31% más alto. La mayoría sigue con menús estáticos.", stat: "31% más ticket promedio" },
      { title: "Las reseñas de clientes quedan sin responder", desc: "El 89% de los comensales lee reseñas antes de ir. Herramientas de IA responden automáticamente con tu voz en minutos — 24/7 — para que no se te escape ninguna.", stat: "89% de comensales lee reseñas" },
      { title: "El marketing es inconsistente y come tiempo", desc: "Los restaurantes que publican 3+ veces/semana reciben el doble de tráfico. Herramientas de IA bajan la preparación de redes de 4 hrs/semana a 20 minutos.", stat: "4 hrs → 20 min/semana" },
      { title: "El desperdicio de comida come márgenes ya finos", desc: "El restaurante promedio desperdicia 4—10% de la comida comprada. El pronóstico de demanda con IA reduce el desperdicio hasta 35%, protegiendo márgenes por ambos lados.", stat: "35% menos desperdicio" },
    ],
    toolEyebrow: "Vista previa del stack",
    toolHeading: "Herramientas Que Tu Blueprint Podría Recomendar",
    toolSub: "Son ejemplos. Tu blueprint elige entre {count}+ herramientas según los problemas, tamaño y presupuesto de tu restaurante.",
    toolStack: [
      { category: "Programación IA", price: "Desde $29/mes", why: "Arma horarios óptimos desde los pronósticos de ventas. Baja la programación de 4 horas a 20 minutos." },
      { category: "Reservas y CRM", price: "Precio personalizado", why: "Gestión de reservas y lista de espera con IA. Reduce las ausencias un 67% con recordatorios automáticos y depósitos inteligentes." },
      { category: "Pedidos Online", price: "Desde $249/mes", why: "Optimización de menú con IA y flujos de upsell suben el ticket promedio online 31%. Se integra con los principales POS." },
      { category: "Marketing y Copy", price: "Desde $39/mes", why: "Genera posts, descripciones de menú y campañas de email en tu voz de marca. Baja la preparación de marketing de 4 hrs a 20 min/semana." },
    ],
    toolMatch: "% match",
    toolFootnote: "Tu blueprint selecciona 5—8 herramientas de una base de datos de {count}+, rankeadas para tu restaurante.",
    getEyebrow: "Qué recibes",
    getHeading: "Tu Blueprint de IA para Restaurante por ${price} Incluye",
    whatYouGet: [
      { title: "El stack exacto de IA para tu restaurante", desc: "5—8 herramientas elegidas según el tipo de restaurante, tamaño y problemas específicos. Con precios reales, pasos de configuración y lo que conviene saltarse." },
      { title: "Hoja de ruta de 90 días", desc: "La Fase 1 ataca tu mayor fuga de ingresos — normalmente ausencias o costo laboral. Tareas semana a semana, no listas interminables." },
      { title: "Librería de prompts para restaurantes", desc: "15—30 prompts para descripciones de menú, posts, respuestas a reseñas, comunicación interna y copy de marketing. Listos para pegar." },
      { title: "Proyecciones de ROI para tu restaurante", desc: "Ahorro laboral esperado, aumento de ingresos por pedidos online y horas ahorradas — armado con benchmarks de restaurantes 2026." },
      { title: "Guía de riesgos y errores comunes", desc: "Qué sale mal cuando los restaurantes adoptan IA demasiado rápido, y cómo evitarlo. Protege tus márgenes mientras modernizas." },
      { title: "Checklist de los primeros 30 días", desc: "Tareas día por día para ver resultados reales en tu primer mes — menos ausencias, programación más rápida o mejores reseñas." },
    ],
    bottomHeading1: "Deja de Perder Plata por Ausencias.",
    bottomHeading2: "Empieza a Manejar un Restaurante Más Inteligente.",
    bottomSub: "Responde 8 preguntas sobre tu restaurante. Recibe un blueprint con tu stack exacto, orden de implementación y hoja de ruta semanal. Listo en minutos.",
    bottomCta: "Arma Mi Blueprint de Restaurante",
    bottomFineReady: "Listo en minutos",
    bottomFineOneTime: "Pago único ${price}",
    bottomFineNoSub: "Sin suscripción",
    guarantee: "Garantía de devolución de 60 días si no aporta valor.",
    preview: {
      eyebrow: "Blueprint de Implementación IA",
      industryLabel: "Restaurante — Gastronomía",
      reportReady: "Reporte Listo",
      kpis: [["$8–38K", "ROI Año 1"], ["6 hrs", "Ahorradas/sem"], ["5–8 tools", "Recomendadas"]],
      topTools: "Top Herramientas Para Tu Restaurante",
      toolBadges: ["Programación — $29/mes", "Reservas — Custom", "Pedidos — $249/mes"],
      roadmap: "Hoja de Ruta 12 Semanas",
      phases: ["Fase 1\nAusencias y Turnos", "Fase 2\nPedidos Online", "Fase 3\nMarketing"],
      floatTop: "Listo en 3 min",
      floatBottom: "Optimizado para restaurantes",
      matchSuffix: "% match",
    },
    faqEyebrow: "Preguntas frecuentes",
    faqHeading: "Antes de armar tu blueprint",
    faq: [
      { q: "Tengo una taquería chica, un fine dining, una dark kitchen o varias unidades. ¿Igual aplica?", a: "Sí. El blueprint te pregunta tu concepto, número de cubiertos y tu mayor dolor — y arma el stack a tu medida. Las dark kitchens reciben herramientas de margen de delivery y agregadores; el full-service recibe reservas y pronóstico de mano de obra; multi-unidad recibe inventario y reportes de operación." },
      { q: "¿Cuánto tarda implementar el blueprint?", a: "Es una hoja de ruta de 12 semanas pensada para dueños-operadores con 2—4 horas a la semana. La Fase 1 ataca tu mayor fuga primero — normalmente mano de obra o costo de alimentos — y la mayoría ve resultados medibles en 2—3 semanas antes de pasar a la siguiente." },
      { q: "Ya probé herramientas de IA y no rindieron. ¿Por qué esto es distinto?", a: "La mayoría compra la herramienta antes de definir el cuello de botella, saltan la integración con POS / reservas y nunca miden el impacto por cubierto. El blueprint invierte eso: rankea por ajuste a tu concepto, mapea cada herramienta a tu POS (Toast, Square, Clover, Lightspeed) y a tu sistema de reservas (Resy, OpenTable, Tock, SevenRooms), y proyecta el ROI por servicio antes de que firmes un contrato." },
      { q: "¿Están seguros los datos de mis clientes y ventas?", a: "Cada recomendación incluye una nota de manejo de datos — dónde se almacenan los datos del cliente, si se comparten con terceros y los focos rojos de PCI y privacidad. Cualquier herramienta turbia en propiedad de datos se marca, no se recomienda en silencio." },
      { q: "¿Y si el blueprint no encaja con mi restaurante?", a: "Garantía de devolución de 60 días, sin preguntas. Nos escribes dentro de los 60 días y te devolvemos todo. Te quedas con el blueprint. Preferimos que te vayas contento antes que cobrarte $149 por un reporte que no pudiste usar." },
    ],
  },
  tr: {
    title: "Restoranlar İçin AI Blueprint — Maliyetleri Düşür, Masaları Doldur",
    metaDesc: "Restoranınız için kişiselleştirilmiş bir AI uygulama blueprint'i alın. Hangi araçları, hangi sırayla kullanacağınızı ve gerçek ROI projeksiyonlarını içerir. 50K+ restoran verisiyle hazırlandı.",
    heroBadge: "Restoran sahipleri için hazırlandı",
    heroTitlePart1: "Hangi AI Araçları Maliyet Düşürüp",
    heroTitlePart2: "Daha Fazla Masa Doldurur?",
    heroSubtitlePre: "Restoranınız hakkında 8 soruyu yanıtlayın. Kişiselleştirilmiş blueprint'iniz size",
    heroSubtitleStrong: "hangi araçları, hangi sırayla kullanacağınızı ve ROI projeksiyonlarını",
    heroSubtitlePost: "sunar — {count} doğrulanmış AI aracı ve 2026 restoran sektörü verileriyle hazırlanır.",
    heroBullets: [
      "AI rezervasyon yönetimiyle gelmeme oranını %67 azaltın",
      "Vardiya planlamasını %80, işgücü maliyetini %23 düşürün",
      "AI upsell akışlarıyla online sipariş sepet ortalamasını %31 artırın",
      "Sosyal medya ve yorum yanıtlarını marka sesinizle otomatikleştirin",
    ],
    heroCta: "Gelmemeleri %67 Azalt",
    heroCtaFine: "Tek seferlik — Abonelik yok — Dakikalar içinde hazır",
    painEyebrow: "Tanıdık geldi mi?",
    painHeading: "Çoğu Restorandaki 6 Kâr Kaybı",
    painSub: "AI bunların hepsini çözebilir — ama hangi aracı, hangi sırayla kullanacağınızı bilirseniz. Blueprint size bunu söylüyor.",
    painPoints: [
      { title: "Gelmeyen müşteriler ve son dakika iptalleri", desc: "Restoranlar masa başına yılda $224 kaybediyor. Akıllı depozito ve otomatik hatırlatmalarla çalışan AI rezervasyon sistemleri gelmeme oranını %67 azaltıyor.", stat: "%67 daha az gelmeme" },
      { title: "Vardiya planlaması manuel kabus", desc: "Fazla personel aylık $3,200 harcama demek. 7shifts gibi AI planlama araçları planlama süresini %80 azaltıp işgücü aşımını %23 kesiyor.", stat: "%23 işgücü maliyet düşüşü" },
      { title: "Online sipariş rakiplere kaybediliyor", desc: "AI ile optimize edilmiş online menü ve upsell akışları kullanan restoranlar %31 daha yüksek sepet ortalaması görüyor. Çoğu restoran hâlâ statik menü kullanıyor.", stat: "%31 daha yüksek sepet" },
      { title: "Müşteri yorumları yanıtsız kalıyor", desc: "Müşterilerin %89'u gelmeden önce yorum okuyor. AI araçları dakikalar içinde, 7/24, sizin sesinizle otomatik yanıt veriyor — hiçbirini kaçırmıyorsunuz.", stat: "%89'u yorum okuyor" },
      { title: "Pazarlama tutarsız ve zaman alıyor", desc: "Haftada 3+ paylaşan restoranlar 2 kat daha fazla yaya trafiği alıyor. AI araçları sosyal medya hazırlığını haftada 4 saatten 20 dakikaya indiriyor.", stat: "4 saat → 20 dk/hafta" },
      { title: "Gıda israfı zaten dar olan marjları yiyor", desc: "Ortalama restoran aldığı gıdanın %4—10'unu israf ediyor. AI talep tahmini israfı %35'e kadar azaltarak iki taraftan da marjı koruyor.", stat: "%35 israf azalması" },
    ],
    toolEyebrow: "Araç stack önizlemesi",
    toolHeading: "Blueprint'inizin Önerebileceği Araçlar",
    toolSub: "Bunlar örnektir. Blueprint'iniz restoranınızın sorunlarına, büyüklüğüne ve bütçesine göre {count}+ araç arasından seçim yapar.",
    toolStack: [
      { category: "AI Vardiya Planlama", price: "$29/ay'dan itibaren", why: "Satış tahminlerinden optimum vardiya oluşturur. Planlama süresini 4 saatten 20 dakikaya indirir." },
      { category: "Rezervasyon ve CRM", price: "Özel fiyatlandırma", why: "AI destekli rezervasyon ve bekleme listesi yönetimi. Otomatik hatırlatmalar ve akıllı depozito akışlarıyla gelmeme oranını %67 azaltır." },
      { category: "Online Sipariş", price: "$249/ay'dan itibaren", why: "AI menü optimizasyonu ve upsell akışları online sepet ortalamasını %31 artırır. Tüm ana POS sistemleriyle entegre olur." },
      { category: "Pazarlama ve Metin", price: "$39/ay'dan itibaren", why: "Sosyal medya paylaşımları, menü açıklamaları ve e-posta kampanyalarını marka sesinizle üretir. Pazarlama hazırlığını haftada 4 saatten 20 dakikaya indirir." },
    ],
    toolMatch: "% eşleşme",
    toolFootnote: "Blueprint'iniz {count}+ araçlık bir veritabanından restoranınıza göre sıralanmış 5—8 araç seçer.",
    getEyebrow: "Ne alıyorsunuz",
    getHeading: "${price} Restoran AI Blueprint'iniz Şunları İçerir",
    whatYouGet: [
      { title: "Restoranınıza özel AI araç stack'i", desc: "Restoran tipinize, büyüklüğünüze ve sorunlarınıza göre 5—8 araç. Gerçek fiyatlandırma, kurulum adımları ve nelerin atlanabileceği dahil." },
      { title: "90 günlük uygulama yol haritası", desc: "1. Faz en büyük gelir kaybınızı hedefler — genellikle gelmeyen müşteriler veya işgücü maliyeti. Haftalara bölünmüş görevler, bunaltıcı listeler değil." },
      { title: "Restoranlara özel prompt kütüphanesi", desc: "Menü açıklamaları, sosyal paylaşımlar, yorum yanıtları, personel iletişimi ve pazarlama metinleri için 15—30 prompt. Yapıştırıp kullanmaya hazır." },
      { title: "Restoranınıza özel ROI projeksiyonları", desc: "Beklenen işgücü tasarrufu, online siparişten gelir artışı ve kazanılan saatler — 2026 restoran sektörü verileriyle hazırlanmıştır." },
      { title: "Risk rehberi ve yaygın hatalar", desc: "Restoranlar AI araçlarını aceleyle benimsediğinde neler ters gidiyor ve nasıl önlenir. Modernleşirken marjlarınızı koruyun." },
      { title: "İlk 30 gün checklist'i", desc: "İlk ay içinde gerçek sonuçları görmek için günlük görevler — daha az gelmeme, daha hızlı vardiya veya daha iyi online yorumlar." },
    ],
    bottomHeading1: "Gelmeyen Müşteriler Yüzünden Para Kaybetmeyi Bırakın.",
    bottomHeading2: "Daha Akıllı Bir Restoran Yönetmeye Başlayın.",
    bottomSub: "Restoranınız hakkında 8 soruyu yanıtlayın. Tam araç stack'i, kurulum sırası ve haftalık yol haritanızla blueprint'inizi alın. Dakikalar içinde hazır.",
    bottomCta: "Restoran AI Blueprint'imi Al",
    bottomFineReady: "Dakikalar içinde hazır",
    bottomFineOneTime: "Tek seferlik ${price}",
    bottomFineNoSub: "Abonelik yok",
    guarantee: "Değer vermezse 60 gün içinde para iadesi garantisi.",
    preview: {
      eyebrow: "AI Uygulama Blueprint",
      industryLabel: "Restoran — Yiyecek & Ağırlama",
      reportReady: "Rapor Hazır",
      kpis: [["$8–38K", "1. Yıl ROI"], ["6 sa", "Haftalık tasarruf"], ["5–8 araç", "Önerilen"]],
      topTools: "Restoranınız İçin En İyi Araçlar",
      toolBadges: ["Vardiya — $29/ay", "Rezervasyon — Özel", "Online Sipariş — $249/ay"],
      roadmap: "12 Haftalık Yol Haritası",
      phases: ["Faz 1\nGelmeme & İşgücü", "Faz 2\nOnline Sipariş", "Faz 3\nPazarlama"],
      floatTop: "3 dakikada hazır",
      floatBottom: "Restorana göre optimize",
      matchSuffix: "% eşleşme",
    },
    faqEyebrow: "Sıkça sorulanlar",
    faqHeading: "Blueprint'inizi oluşturmadan önce",
    faq: [
      { q: "Küçük bir lokanta, fine-dining, ghost kitchen veya çok şubeli bir konseptim var. Yine de işe yarar mı?", a: "Evet. Blueprint size konseptinizi, kuver sayınızı ve en büyük sıkıntınızı sorar ve stack'i ona göre özelleştirir. Ghost kitchen'lar teslimat marjı ve agregatör araçları alır; masa servisi rezervasyon ve işgücü tahmin araçları alır; çok şubeli işletmeler envanter-senkron ve operasyon raporlama araçları alır." },
      { q: "Blueprint'i uygulamak ne kadar sürer?", a: "Haftada 2—4 saat ayırabilen sahip-işletmeciler için tasarlanmış 12 haftalık bir yol haritasıdır. 1. Faz önce en büyük kaybınıza — genellikle işgücü planlama veya gıda maliyeti — odaklanır ve çoğu restoran bir sonraki faza geçmeden önce 2—3 hafta içinde ölçülebilir sonuç görür." },
      { q: "AI araçlarını denedim ama getiri olmadı. Bunun farkı ne?", a: "Çoğu işletmeci darboğazı tanımlamadan aracı satın alır, POS / rezervasyon entegrasyonunu atlar ve kuver başına maliyet etkisini hiç ölçmez. Blueprint bunu tersine çevirir: araçları konseptinize uygunluğa göre sıralar, her birini POS'unuza (Toast, Square, Clover, Lightspeed) ve rezervasyon sisteminize (Resy, OpenTable, Tock, SevenRooms) eşler ve sözleşme imzalamadan önce servis başına ROI öngörür." },
      { q: "Müşteri ve satış verilerim güvende mi?", a: "Her öneride veri işleme notu vardır — müşteri verisinin nerede tutulduğu, üçüncü taraflarla paylaşılıp paylaşılmadığı ve PCI / gizlilik kırmızı bayrakları. Veri sahipliği şüpheli görünen her şey işaretlenir, sessizce önerilmez." },
      { q: "Blueprint restoranıma uymazsa ne olur?", a: "60 gün içinde koşulsuz para iade garantisi. 60 gün içinde bize yazarsınız, tam iade yaparız. Blueprint sizde kalır. 149 dolarınızı tutup mutsuz göndermektense kullanamayacağınız bir rapor için iade etmeyi tercih ederiz." },
    ],
  },
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const RestaurantBlueprintPreview = ({ c }) => (
  <div className="relative select-none">
    <div className="absolute -inset-4 bg-gradient-to-br from-orange-600/20 via-red-600/20 to-amber-600/20 rounded-3xl blur-2xl" />
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/90 border border-white/10 shadow-2xl backdrop-blur-sm">
      <div className="bg-slate-900 px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-orange-400 font-semibold tracking-widest uppercase mb-0.5">{c.preview.eyebrow}</div>
          <div className="text-sm font-bold text-white">{c.preview.industryLabel}</div>
        </div>
        <span className="text-[10px] font-semibold bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2.5 py-1 rounded-full">{c.preview.reportReady}</span>
      </div>
      <div className="grid grid-cols-3 border-b border-white/10">
        {c.preview.kpis.map(([val, lab]) => (
          <div key={lab} className="px-4 py-3 text-center border-r last:border-r-0 border-white/10">
            <div className="text-base font-bold text-orange-400">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.topTools}</div>
        {[
          { name: "7shifts", badge: c.preview.toolBadges[0], score: 93, color: "bg-orange-500" },
          { name: "SevenRooms", badge: c.preview.toolBadges[1], score: 89, color: "bg-blue-500" },
          { name: "Olo", badge: c.preview.toolBadges[2], score: 85, color: "bg-blue-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/10/40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className={`h-full ${tool.color} rounded-full`} initial={{ width: 0 }} animate={{ width: `${tool.score}%` }} transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }} />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}{c.preview.matchSuffix}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.roadmap}</div>
        <div className="flex gap-2">
          {c.preview.phases.map((p, i) => (
            <div key={p} className={`flex-1 text-center rounded-lg py-2 px-1 text-[10px] font-medium border ${i === 0 ? "bg-orange-500/20 border-orange-500/40 text-orange-400" : "bg-white/8 border-white/10 text-slate-500"}`}>
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
    <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-4 -right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-blue-500/40">
      {c.preview.floatTop}
    </motion.div>
    <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute -bottom-3 -left-4 bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-orange-600/40">
      {c.preview.floatBottom}
    </motion.div>
  </div>
);

const RestaurantPage = () => {
  const navigate = useNavigate();
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;
  const [currentPrice, setCurrentPrice] = useState(149);
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  const goToAudit = () => navigate("/audit?industry=restaurant");

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path="/restaurant"
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        faqItems={c.faq || c.faqs}
        industry="Restaurant"
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-orange-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-red-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-semibold px-4 py-2 rounded-full mb-8">
                <Utensils className="w-4 h-4" /> {c.heroBadge}
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
                <span className="text-white">{c.heroTitlePart1}</span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-red-400 to-amber-400 bg-clip-text text-transparent">{c.heroTitlePart2}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl">
                {c.heroSubtitlePre}{" "}
                <span className="text-white font-semibold">{c.heroSubtitleStrong}</span>{" "}
                {c.heroSubtitlePost.replace('{count}', displayCount)}
              </motion.p>
              <motion.ul initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="space-y-3 mb-10">
                {c.heroBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </motion.ul>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-col sm:flex-row gap-4 items-start">
                <Button onClick={goToAudit} size="lg" className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-orange-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  {c.heroCta} — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">{c.heroCtaFine}</p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7 }} className="hidden lg:block">
              <RestaurantBlueprintPreview c={c} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-orange-400 font-semibold tracking-widest uppercase mb-3">{c.painEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.painHeading}</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">{c.painSub}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.painPoints.map((pp, i) => {
                const Icon = PAIN_ICONS[i];
                const s = PAIN_STYLES[i];
                return (
                  <motion.div key={pp.title} variants={fadeUp} className={`rounded-2xl border p-6 ${s.bg} ${s.border}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg} border ${s.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${s.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${s.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOOL PREVIEW */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-blue-400 font-semibold tracking-widest uppercase mb-3">{c.toolEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.toolHeading}</h2>
              <p className="text-slate-400 max-w-xl mx-auto">{c.toolSub.replace('{count}', displayCount)}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.toolStack.map((tool, i) => {
                const meta = TOOL_META[i];
                return (
                  <motion.div key={meta.name} variants={fadeUp} className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">{meta.name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                        <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                        <span className="text-xs text-slate-400 font-semibold">{meta.score}{c.toolMatch}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                    <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className={`h-full ${meta.color} rounded-full`} initial={{ width: 0 }} whileInView={{ width: `${meta.score}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3, ease: "easeOut" }} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">{c.toolFootnote.replace('{count}', displayCount)}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">{c.getEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.getHeading.replace('${price}', `$${currentPrice}`)}</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.whatYouGet.map((item) => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <div className="w-8 h-8 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-10 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-500 text-sm">{c.guarantee}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.faqEyebrow}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">{c.faqHeading}</h2>
          </div>
          <div className="space-y-3">
            {c.faq.map((item, i) => (
              <details key={i} className="group bg-slate-900/70 border border-white/10 rounded-2xl px-5 py-4 transition-colors hover:border-blue-500/30">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm leading-snug">{item.q}</span>
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl flex-shrink-0">+</span>
                </summary>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/8">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 rounded-2xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center mx-auto mb-8">
              <Utensils className="w-8 h-8 text-orange-400" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              {c.bottomHeading1}
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">{c.bottomHeading2}</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              {c.bottomSub}
            </p>
            <Button onClick={goToAudit} size="lg" className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-black text-xl px-12 py-7 rounded-2xl shadow-xl shadow-orange-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
              {c.bottomCta} — ${currentPrice} <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-6 mt-8">
              {[
                { icon: Clock, text: c.bottomFineReady },
                { icon: DollarSign, text: c.bottomFineOneTime.replace('${price}', `$${currentPrice}`) },
                { icon: CheckCircle, text: c.bottomFineNoSub },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm"><Icon className="w-4 h-4" /><span>{text}</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RestaurantPage;
